import React from "react";
import { AtSign, Check, Copy, Facebook, Linkedin, Mail, MessageCircle, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function ShareActions({ url, title, text = "" }) {
  const [copied, setCopied] = React.useState(false);
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedText = encodeURIComponent(text ? `${title}\n\n${text}` : title);

  const copyLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement("input");
        input.value = url;
        input.setAttribute("readonly", "");
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        input.remove();
      }
      setCopied(true);
      toast.success("Link copied to clipboard");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const openShareWindow = (shareUrl) => {
    window.open(shareUrl, "_blank", "noopener,noreferrer,width=700,height=650");
  };

  const useNativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({ title, text, url });
    } catch (error) {
      if (error?.name !== "AbortError") toast.error("Could not open the share dialog");
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="share-actions">
      {navigator.share && (
        <button type="button" onClick={useNativeShare} className="btn-primary text-xs" data-testid="native-share">
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>
      )}
      <button type="button" onClick={copyLink} className="btn-outline text-xs" data-testid="copy-content-link">
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        {copied ? "Copied" : "Copy link"}
      </button>
      <button type="button" onClick={() => openShareWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`)} className="btn-outline text-xs" aria-label="Share on LinkedIn" title="Share on LinkedIn" data-testid="share-linkedin">
        <Linkedin className="w-3.5 h-3.5" /> LinkedIn
      </button>
      <button type="button" onClick={() => openShareWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`)} className="btn-outline text-xs" aria-label="Share on Facebook" title="Share on Facebook" data-testid="share-facebook">
        <Facebook className="w-3.5 h-3.5" /> Facebook
      </button>
      <button type="button" onClick={() => openShareWindow(`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`)} className="btn-outline text-xs" aria-label="Share on X" title="Share on X" data-testid="share-x">
        <AtSign className="w-3.5 h-3.5" /> X
      </button>
      <button type="button" onClick={() => openShareWindow(`https://wa.me/?text=${encodedText}%20${encodedUrl}`)} className="btn-outline text-xs" aria-label="Share on WhatsApp" title="Share on WhatsApp" data-testid="share-whatsapp">
        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
      </button>
      <a href={`mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}`} className="btn-outline text-xs" data-testid="share-email">
        <Mail className="w-3.5 h-3.5" /> Email
      </a>
    </div>
  );
}
